import React from 'react';
import {
  Column,
  Copy,
  Flex,
  Stack,
  SvgReceipt,
} from '@grubhubprod/gh-cookbook-react/dist';
import { Row, Squish, SvgInfo } from '@grubhubprod/gh-cookbook-react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { IOrdersHistory, IOrdersHistoryDiner } from 'src/types/OrderHistory';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useFormattedDate } from 'src/components/enterprise/features/OrderHistory/OrderList/hooks/useFormattedDate';
import { getDisplayOrderType } from 'src/common/Order/utils';
import { RouterLink } from 'src/components/RouterLink/RouterLink';

export enum OrderTypeApiValuesEnum {
  STANDARD = 'STANDARD',
  CATERING = 'CATERING',
  GROUP = 'GROUP',
}

export enum AvailableOrderTypesEnum {
  STANDARD = 'Regular',
  CATERING = 'Catering',
  GROUP = 'Team',
}

export interface TableRowProps {
  order: IOrdersHistory;
  corpId: string;
  isCorpExpenseEnabled: boolean;
  shouldDisplayDepartments: boolean;
  shouldDisplayLocations: boolean;
}

export default function TableRow({
  order,
  corpId,
  isCorpExpenseEnabled,
  shouldDisplayDepartments,
  shouldDisplayLocations,
}: TableRowProps): JSX.Element {
  const { formattedDate } = useFormattedDate(
    order.delivery_on_utc,
    order.time_zone
  );

  return (
    <Squish
      as="li"
      key={order.order_id}
      inset="md"
      paddingLeft={0}
      paddingRight={0}
      borderBottom="1px solid"
      borderColor="lightLine"
    >
      <Row gutter={4} alignItems="center" data-testid="order-row">
        <Column phone={2} gutter={4}>
          {formattedDate}
        </Column>
        <Column phone={2} gutter={4} className="ellipsisOverflow">
          {order.ordered_by.name}
        </Column>
        <Column phone={1} gutter={4}>
          <Flex alignItems="center">
            <Stack x="xxx-sm">
              {currencyFormatter(
                Number(transformPenniesToDollar(order.total_charge))
              )}
            </Stack>
            <AllocationInfoTooltip
              participants={order.allocation_participants}
            />
          </Flex>
        </Column>
        <Column phone={1} className="ellipsisOverflow" gutter={4}>
          <div>
            {getDisplayOrderType({
              isGroupOrder: order.group_order,
              orderType: AvailableOrderTypesEnum[order.order_type],
            })}
          </div>
          {AvailableOrderTypesEnum[order.order_type] ===
            AvailableOrderTypesEnum.GROUP && (
            <Copy variant="caption">{get(order.group_order_info, 'name')}</Copy>
          )}
        </Column>
        {shouldDisplayDepartments && (
          <Column
            aria-label="department"
            style={{ flex: '3 1' }}
            className="ellipsisOverflow"
            gutter={8}
          >
            {order.department}
          </Column>
        )}
        {shouldDisplayLocations && (
          <Column
            aria-label="location"
            gutter={8}
            style={{ flex: '3 1' }}
            className="ellipsisOverflow"
          >
            {order.location}
          </Column>
        )}
        {isCorpExpenseEnabled && (
          <Column
            aria-label="expense_code"
            style={{ flex: '3 1' }}
            className="ellipsisOverflow"
            gutter={8}
          >
            {order.ordered_by.expense_code}
          </Column>
        )}
        <Column style={{ flex: '3 1' }} className="ellipsisOverflow" gutter={4}>
          {order.restaurant_name}
        </Column>
        <Column
          gutter={0}
          style={{ textAlign: 'end', flex: '1 1' }}
          data-testid="order-history-receipt-link"
        >
          <RouterLink
            href={`/corporate/enterprise/${corpId}/order-receipt/${order.order_id}`}
          >
            <SvgReceipt size="24px" aria-label="View receipt" />
          </RouterLink>
        </Column>
      </Row>
    </Squish>
  );
}

function AllocationInfoTooltip({
  participants = [],
}: {
  participants: IOrdersHistoryDiner[];
}): JSX.Element {
  if (participants.length < 2) {
    return null;
  }
  return (
    <Tooltip
      offset={[0, 10]}
      maxWidth="290px"
      roundedContainer
      content={
        <Copy variant="body" color="invertedPrimaryText">
          Allocation used on this order. View receipt to see allocation details.
        </Copy>
      }
    >
      <span>
        <Copy variant="body" color="secondaryText">
          <Flex alignItems="center">
            <SvgInfo size="16px" />
          </Flex>
        </Copy>
      </span>
    </Tooltip>
  );
}
